import { useState, useEffect, useRef, useMemo } from "react";
import { X, Upload, MapPin, Calendar as CalendarIcon, Clock, Users, Tag, DollarSign, Loader2, ChevronDown, Hash, UserCheck } from "lucide-react";
import JoditEditor from 'jodit-react';
import { step1Schema, step2Schema, step3Schema } from "../formValidators/createEvent.js";
import { validateSchema } from "../lib/utils.js";

export function AddEventModal({ isOpen, onClose, onSubmit, initialData = null, isLoading = false }) {
  // Initial state templates
  const initialStep1 = {
    title: "",
    description: "",
    shortDescription: "",
    date: "",
    time: "",
    thumbnail: null,
    thumbnailPreview: "",
  };

  const initialStep2 = {
    pincode: "",
    country: "",
    city: "",
    state: "",
    location: "",
    whatToExpect: "",
    tags: [],
  };

  const initialStep3 = {
    audience: "all",
    age: {
      minAge: "",
      maxAge: ""
    },
    capacity: "",
    malePrice: {
      price: "",
      discountedPrice: ""
    },
    femalePrice: {
      price: "",
      discountedPrice: ""
    },
    communityLinks: "",
    currency: "INR",
    status: "draft",
    showDummyUsers: false, // Added switch field
    appliedGst: true,
  };

  // Separate state for each step
  const [step1Data, setStep1Data] = useState(initialStep1);
  const [step2Data, setStep2Data] = useState(initialStep2);
  const [step3Data, setStep3Data] = useState(initialStep3);

  // Combined form data
  const [formData, setFormData] = useState({
    coordinates: { type: "Point", coordinates: [0, 0] },
  });

  // Dropdown data states
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Loading states for dropdowns
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  const [errors, setErrors] = useState({});
  const [currentTag, setCurrentTag] = useState("");
  const [step, setStep] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const totalSteps = 3;
  const fileInputRef = useRef(null);

  // Jodit Editor Configuration
  const joditConfig = useMemo(() => ({
    readonly: false,
    placeholder: 'Start typing...',
    height: 250,
    toolbar: true,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|',
      'fontsize', 'brush', '|',
      'align', '|',
      'link', '|',
      'undo', 'redo'
    ],
    removeButtons: ['image', 'video', 'file', 'fullsize', 'source'],
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    minHeight: 200,
    maxHeight: 400,
    style: {
      font: 'inherit',
    },
    enter: 'P',
    defaultActionOnPaste: 'insert_as_html',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
  }), []);

  // Jodit config for What to Expect (shorter)
  const joditConfigShort = useMemo(() => ({
    ...joditConfig,
    height: 180,
    minHeight: 150,
    maxHeight: 300,
  }), [joditConfig]);

  // Fetch Countries on component mount
  useEffect(() => {
    fetchCountries();
    fetchCurrencies();
  }, []);

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Edit mode - populate with existing data
        setStep1Data({
          title: initialData.title || "",
          description: initialData.description || "",
          shortDescription: initialData.shortDescription || "",
          date: initialData.date ? new Date(initialData.date).toISOString().split('T')[0] : "",
          time: initialData.time || "",
          thumbnail: null,
          thumbnailPreview: initialData.thumbnail || "",
        });

        setStep2Data({
          pincode: initialData.pincode || "",
          country: initialData.country || "",
          city: initialData.city || "",
          state: initialData.state || "",
          venue: initialData.venue || "",
          location: initialData.location || "",
          whatToExpect: initialData.whatToExpect || "",
          tags: initialData.tags || [],
        });

        setStep3Data({
          audience: initialData.audience || "all",
          age: {
            minAge: initialData.age?.minAge || "",
            maxAge: initialData.age?.maxAge || ""
          },
          capacity: initialData.capacity || "",
          malePrice: {
            price: initialData.malePrice?.price || "",
            discountedPrice: initialData.malePrice?.discountedPrice || ""
          },
          femalePrice: {
            price: initialData.femalePrice?.price || "",
            discountedPrice: initialData.femalePrice?.discountedPrice || ""
          },
          communityLinks: initialData.communityLinks || "",
          currency: initialData.currency || "INR",
          status: initialData.status || "draft",
          showDummyUsers: initialData.showDummyUsers || false,
          appliedGst: initialData.appliedGst ?? true,
        });

        console.log("step3Data",initialData.communityLinks)

        setFormData({
          coordinates: initialData.coordinates || { type: "Point", coordinates: [0, 0] },
        });

        // Fetch dependent dropdowns if editing
        if (initialData.country) {
          fetchStates(initialData.country).then(() => {
            if (initialData.state) {
              setTimeout(() => {
                fetchCities(initialData.country, initialData.state);
              }, 300);
            }
          });
        }
      } else {
        // Create mode - reset to initial values
        resetForm();
      }
      setStep(1);
      setErrors({});
    }
  }, [isOpen, initialData]);

  console.log("step2Data--->",step2Data);
  console.log("step3Data--->",step3Data);

  // Reset form function
  const resetForm = () => {
    setStep1Data(initialStep1);
    setStep2Data(initialStep2);
    setStep3Data(initialStep3);
    setFormData({ coordinates: { type: "Point", coordinates: [0, 0] } });
    setErrors({});
    setCurrentTag("");
    setStep(1);
    setStates([]);
    setCities([]);
  };

  // Fetch countries
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await fetch("https://api-csc.indraq.info/api/countries");
      const data = await response.json();
      console.log("Countries:", data);
      setCountries(data.data.map(({ name, iso2 }) => ({ code: iso2, name })));
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([
        { code: "IN", name: "India" },
        { code: "US", name: "United States" },
        { code: "GB", name: "United Kingdom" },
      ]);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Fetch states based on selected country
  const fetchStates = async (countryCode) => {
    setLoadingStates(true);
    setStates([]);
    setCities([]);

    try {
      const country = countries.find((c) => c.name === countryCode);
      if (!country) {
        console.error("Country not found for code:", countryCode);
        setLoadingStates(false);
        return;
      }

      console.log("Fetching states for country:", country.name);
      const response = await fetch(
        `https://api-csc.indraq.info/api/states?country_name=${country.name}`
      );
      const data = await response.json();
      console.log("States:", data);

      if (data.data) {
        setStates(data.data.map(({ name, iso2 }) => ({ code: iso2, name })));
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch cities based on selected state
  const fetchCities = async (countryCode, stateCode) => {
    setLoadingCities(true);
    setCities([]);

    try {
      const country = countries.find((c) => c.name === countryCode);
      const state = states.find((s) => s.name === stateCode);

      if (!country || !state) {
        console.error("Country or state not found:", { countryCode, stateCode });
        setLoadingCities(false);
        return;
      }

      console.log("Fetching cities for:", country.name, state.name);
      const response = await fetch(
        `https://api-csc.indraq.info/api/cities?country_name=${country.name}&state_name=${state.name}`
      );
      const data = await response.json();
      console.log("Cities:", data);

      if (data.data) {
        setCities(data.data.map(({ name }) => ({ code: name, name })));
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Fetch currencies
  const fetchCurrencies = async () => {
    setLoadingCurrencies(true);
    try {
      setCurrencies([
        { code: "INR", name: "Indian Rupee", symbol: "₹" },
        { code: "USD", name: "US Dollar", symbol: "$" },
        { code: "EUR", name: "Euro", symbol: "€" },
        { code: "GBP", name: "British Pound", symbol: "£" },
        { code: "JPY", name: "Japanese Yen", symbol: "¥" },
        { code: "AUD", name: "Australian Dollar", symbol: "A$" },
        { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
        { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
        { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
        { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
      ]);
    } catch (error) {
      console.error("Error fetching currencies:", error);
    } finally {
      setLoadingCurrencies(false);
    }
  };

  // Handle country change
  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    console.log("CountryCode--->", countryCode);
    setStep2Data((prev) => ({ ...prev, country: countryCode, state: "", city: "" }));

    if (countryCode) {
      fetchStates(countryCode);
    } else {
      setStates([]);
      setCities([]);
    }

    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: "" }));
    }
  };

  // Handle state change
  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    console.log(stateCode);
    setStep2Data((prev) => ({ ...prev, state: stateCode, city: "" }));

    if (stateCode && step2Data.country) {
      fetchCities(step2Data.country, stateCode);
    } else {
      setCities([]);
    }

    if (errors.state) {
      setErrors((prev) => ({ ...prev, state: "" }));
    }
  };

  // Handle city change
  const handleCityChange = (e) => {
    const cityCode = e.target.value;
    setStep2Data((prev) => ({ ...prev, city: cityCode }));

    if (errors.city) {
      setErrors((prev) => ({ ...prev, city: "" }));
    }
  };

  // Handle input change for current step
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const processedValue = type === "number" ? parseFloat(value) || 0 : value;

    if (step === 1) {
      setStep1Data((prev) => ({ ...prev, [name]: processedValue }));
    } else if (step === 2) {
      setStep2Data((prev) => ({ ...prev, [name]: processedValue }));
    } else if (step === 3) {
      setStep3Data((prev) => ({ ...prev, [name]: processedValue }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle toggle switch for showDummyUsers
  const handleToggleSwitch = () => {
    setStep3Data((prev) => ({
      ...prev,
      showDummyUsers: !prev.showDummyUsers
    }));
  };

  const handlegstToggleSwitch = () => {
    setStep3Data((prev) => ({
      ...prev,
      appliedGst: !prev.appliedGst
    }));
  };

  // Handle age change
  const handleAgeChange = (e, field) => {
    const value = e.target.value;
    
    setStep3Data((prev) => ({
      ...prev,
      age: {
        ...prev.age,
        [field]: value
      }
    }));

    if (errors[`age.${field}`]) {
      setErrors((prev) => ({ ...prev, [`age.${field}`]: "" }));
    }
  };

  // Handle nested price change (for malePrice and femalePrice)
  const handlePriceChange = (e, priceType, field) => {
    const value = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
    
    setStep3Data((prev) => ({
      ...prev,
      [priceType]: {
        ...prev[priceType],
        [field]: value
      }
    }));

    if (errors[`${priceType}.${field}`]) {
      setErrors((prev) => ({ ...prev, [`${priceType}.${field}`]: "" }));
    }
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  // Process file (used by both click and drag-drop)
  const processFile = (file) => {
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({ ...prev, thumbnail: "Please select a valid image file" }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, thumbnail: "Image size must be less than 5MB" }));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setStep1Data((prev) => ({
        ...prev,
        thumbnail: file,
        thumbnailPreview: reader.result,
      }));
      setErrors((prev) => ({ ...prev, thumbnail: "" }));
    };
    reader.readAsDataURL(file);
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  // Remove thumbnail
  const removeThumbnail = () => {
    setStep1Data((prev) => ({
      ...prev,
      thumbnail: null,
      thumbnailPreview: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle tag addition
  const addTag = () => {
    if (currentTag.trim() && step2Data.tags.length < 20) {
      if (!step2Data.tags.includes(currentTag.trim())) {
        setStep2Data((prev) => ({
          ...prev,
          tags: [...prev.tags, currentTag.trim()],
        }));
      }
      setCurrentTag("");
    }
  };

  // Handle tag removal
  const removeTag = (tagToRemove) => {
    setStep2Data((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  // Validate current step
  const validateStep = () => {
    let schema;
    let data;
    let isValid = true;
    const newErrors = {};

    if (step === 1) {
      console.log("step1Schema-->", step1Data);
      schema = step1Schema;
      data = step1Data;

      // Validate date
      if (data.date) {
        const eventDate = new Date(data.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (eventDate < today) {
          newErrors.date = "Event date must be in the present or future";
          isValid = false;
        }
      }

      // Validate thumbnail exists
      if (!data.thumbnail && !data.thumbnailPreview) {
        newErrors.thumbnail = "Event thumbnail is required";
        isValid = false;
      }

      // Set errors if validation failed
      if (!isValid) {
        setErrors(newErrors);
        return false;
      }
    } else if (step === 2) {
      schema = step2Schema;
      data = step2Data;
    } else if (step === 3) {
      schema = step3Schema;
      data = step3Data;

      // Additional validation for discounted prices
      if (data.malePrice.discountedPrice && data.malePrice.price) {
        if (parseFloat(data.malePrice.discountedPrice) > parseFloat(data.malePrice.price)) {
          newErrors['malePrice.discountedPrice'] = "Discounted price cannot be greater than original price";
          isValid = false;
        }
      }

      if (data.femalePrice.discountedPrice && data.femalePrice.price) {
        if (parseFloat(data.femalePrice.discountedPrice) > parseFloat(data.femalePrice.price)) {
          newErrors['femalePrice.discountedPrice'] = "Discounted price cannot be greater than original price";
          isValid = false;
        }
      }

      // Validate age restrictions
      if (data.age.minAge && data.age.maxAge) {
        if (parseInt(data.age.minAge) > parseInt(data.age.maxAge)) {
          newErrors['age.minAge'] = "Minimum age cannot be greater than maximum age";
          isValid = false;
        }
      }

      if (data.age.minAge && parseInt(data.age.minAge) < 0) {
        newErrors['age.minAge'] = "Age cannot be negative";
        isValid = false;
      }

      if (data.age.maxAge && parseInt(data.age.maxAge) < 0) {
        newErrors['age.maxAge'] = "Age cannot be negative";
        isValid = false;
      }

      if (!isValid) {
        setErrors(newErrors);
        return false;
      }
    }

    const result = validateSchema(schema, data, setErrors);
    return result.success && isValid;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep()) {
      if (step < totalSteps) {
        setStep(step + 1);
        setErrors({});
      }
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("hello")
    if (!validateStep()) {
      console.log(validateStep())
      return;
    }

    // Convert empty strings to 0 for prices and null for ages
    const processedStep3Data = {
      ...step3Data,
      communityLinks: step3Data.communityLinks?.trim() ? step3Data.communityLinks.trim() : null,
      age: {
        minAge: step3Data.age.minAge === "" ? null : step3Data.age.minAge,
        maxAge: step3Data.age.maxAge === "" ? null : step3Data.age.maxAge
      },
      malePrice: {
        price: step3Data.malePrice.price === "" ? 0 : parseFloat(step3Data.malePrice.price),
        discountedPrice: step3Data.malePrice.discountedPrice === "" ? 0 : parseFloat(step3Data.malePrice.discountedPrice)
      },
      femalePrice: {
        price: step3Data.femalePrice.price === "" ? 0 : parseFloat(step3Data.femalePrice.price),
        discountedPrice: step3Data.femalePrice.discountedPrice === "" ? 0 : parseFloat(step3Data.femalePrice.discountedPrice)
      }
    };

    const finalFormData = {
      ...formData,
      ...step1Data,
      ...step2Data,
      ...processedStep3Data,
      communityLinks: processedStep3Data.communityLinks,
    };
    console.log(finalFormData);

    onSubmit(finalFormData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1B3C5C]/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-[#F4A696]/20 bg-gradient-to-r from-[#FFF4F0] to-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#1B3C5C]">
                {initialData ? "Edit Event" : "Create New Event"}
              </h2>
              <p className="mt-1 text-sm text-[#1B3C5C]/60">
                Step {step} of {totalSteps}
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-[#1B3C5C]/60 hover:bg-[#F4A696]/10 hover:text-[#F4A696] transition-colors"
              disabled={isLoading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[#FFF4F0]">
            <div
              className="h-full bg-gradient-to-r from-[#F4A696] to-[#E89580] transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-200px)] px-6 py-6">
          {/* Step 1: Basic Information */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in-up">
              <h3 className="text-lg font-semibold text-[#1B3C5C] flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-[#F4A696]" />
                Basic Information
              </h3>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Event Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={step1Data.title}
                  onChange={handleChange}
                  placeholder="Enter event title"
                  maxLength={200}
                  className={`w-full rounded-xl border ${
                    errors.title ? "border-red-500" : "border-[#F4A696]/30"
                  } bg-white px-4 py-3 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20`}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>

              {/* Short Description */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  name="shortDescription"
                  value={step1Data.shortDescription}
                  onChange={handleChange}
                  placeholder="Brief one-liner about your event"
                  maxLength={200}
                  className="w-full rounded-xl border border-[#F4A696]/30 bg-white px-4 py-3 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
                />
                {errors.shortDescription && (
                  <p className="mt-1 text-sm text-red-500">{errors.shortDescription}</p>
                )}
              </div>

              {/* Description - Jodit Editor */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Full Description
                </label>
                <div className={`rounded-xl border ${
                  errors.description ? "border-red-500" : "border-[#F4A696]/30"
                } overflow-hidden jodit-custom-wrapper`}>
                  <JoditEditor
                    value={step1Data.description}
                    config={joditConfig}
                    onBlur={(newContent) => {
                      setStep1Data((prev) => ({ ...prev, description: newContent }));
                      if (errors.description) {
                        setErrors((prev) => ({ ...prev, description: "" }));
                      }
                    }}
                    onChange={() => {}} // Required but we use onBlur
                  />
                </div>
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                )}
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                    Event Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696]" />
                    <input
                      type="date"
                      name="date"
                      value={step1Data.date}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full rounded-xl border ${
                        errors.date ? "border-red-500" : "border-[#F4A696]/30"
                      } bg-white pl-10 pr-4 py-3 text-[#1B3C5C] focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20`}
                    />
                  </div>
                  {errors.date && (
                    <p className="mt-1 text-sm text-red-500">{errors.date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                    Event Time <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696]" />
                    <input
                      type="time"
                      name="time"
                      value={step1Data.time}
                      onChange={handleChange}
                      className={`w-full rounded-xl border ${
                        errors.time ? "border-red-500" : "border-[#F4A696]/30"
                      } bg-white pl-10 pr-4 py-3 text-[#1B3C5C] focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20`}
                    />
                  </div>
                  {errors.time && (
                    <p className="mt-1 text-sm text-red-500">{errors.time}</p>
                  )}
                </div>
              </div>

              {/* Thumbnail Drag & Drop / File Upload */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Event Thumbnail <span className="text-red-500">*</span>
                </label>
                <div
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !step1Data.thumbnailPreview && fileInputRef.current?.click()}
                  className={`relative cursor-pointer rounded-xl border-2 border-dashed ${
                    isDragging ? "border-[#F4A696] bg-[#FFF4F0]" :
                    errors.thumbnail ? "border-red-300 bg-red-50" :
                    "border-gray-300 bg-white hover:border-[#F4A696]/50 hover:bg-[#FFF4F0]/30"
                  } transition-all duration-200 ${step1Data.thumbnailPreview ? "p-2" : "p-12"}`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  {!step1Data.thumbnailPreview ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="rounded-full bg-[#FFF4F0] p-6">
                        <Upload className={`h-12 w-12 ${
                          isDragging ? "text-[#F4A696] animate-bounce" : "text-[#F4A696]/60"
                        }`} />
                      </div>
                      <div className="text-center">
                        <p className="text-base font-semibold text-[#1B3C5C]">
                          Drag and Drop here
                        </p>
                        <p className="mt-1 text-sm text-[#1B3C5C]/60">or</p>
                        <p className="mt-1 text-lg font-medium text-[#3B82F6] cursor-pointer hover:text-[#2563EB]">
                          Browse files
                        </p>
                      </div>
                      <p className="text-xs text-[#1B3C5C]/50">
                        Accepted File Types: .jpg, .png, .gif only
                      </p>
                      <div className="flex items-center gap-2 text-xs text-[#1B3C5C]/50">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Secure
                      </div>
                    </div>
                  ) : (
                    <div className="relative group">
                      <img
                        src={step1Data.thumbnailPreview}
                        alt="Preview"
                        className="w-full h-80 object-contain rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-4">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                          className="rounded-full bg-white p-4 text-[#F4A696] hover:bg-[#F4A696] hover:text-white transition-colors shadow-lg"
                        >
                          <Upload className="h-6 w-6" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeThumbnail();
                          }}
                          className="rounded-full bg-white p-4 text-red-500 hover:bg-red-500 hover:text-white transition-colors shadow-lg"
                        >
                          <X className="h-6 w-6" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {errors.thumbnail && (
                  <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.thumbnail}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Location Details */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in-up">
              <h3 className="text-lg font-semibold text-[#1B3C5C] flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#F4A696]" />
                Location Details
              </h3>

              {/* Pincode Field */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Pincode / Postal Code <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696]" />
                  <input
                    type="text"
                    name="pincode"
                    value={step2Data.pincode}
                    onChange={handleChange}
                    placeholder="Enter pincode"
                    maxLength={10}
                    className={`w-full rounded-xl border ${
                      errors.pincode ? "border-red-500" : "border-[#F4A696]/30"
                    } bg-white pl-10 pr-4 py-3 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20`}
                  />
                </div>
                {errors.pincode && (
                  <p className="mt-1 text-sm text-red-500">{errors.pincode}</p>
                )}
              </div>

              {/* Country, State, City Dropdowns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Country Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="country"
                      value={step2Data.country}
                      onChange={handleCountryChange}
                      disabled={loadingCountries}
                      className={`w-full rounded-xl border ${
                        errors.country ? "border-red-500" : "border-[#F4A696]/30"
                      } bg-white px-4 py-3 pr-10 text-[#1B3C5C] appearance-none focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                    >
                      <option value="">
                        {loadingCountries ? "Loading..." : "Select Country"}
                      </option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    {loadingCountries ? (
                      <Loader2 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] animate-spin" />
                    ) : (
                      <ChevronDown className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] pointer-events-none" />
                    )}
                  </div>
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-500">{errors.country}</p>
                  )}
                </div>

                {/* State Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="state"
                      value={step2Data.state}
                      onChange={handleStateChange}
                      disabled={!step2Data.country || loadingStates}
                      className={`w-full rounded-xl border ${
                        errors.state ? "border-red-500" : "border-[#F4A696]/30"
                      } bg-white px-4 py-3 pr-10 text-[#1B3C5C] appearance-none focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                    >
                      <option value="">
                        {!step2Data.country ? "Select country first" :
                         loadingStates ? "Loading..." : "Select State"}
                      </option>
                      {states.map((state) => (
                        <option key={state.code} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {loadingStates ? (
                      <Loader2 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] animate-spin" />
                    ) : (
                      <ChevronDown className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] pointer-events-none" />
                    )}
                  </div>
                  {errors.state && (
                    <p className="mt-1 text-sm text-red-500">{errors.state}</p>
                  )}
                </div>

                {/* City Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="city"
                      value={step2Data.city}
                      onChange={handleCityChange}
                      disabled={!step2Data.state || loadingCities}
                      className={`w-full rounded-xl border ${
                        errors.city ? "border-red-500" : "border-[#F4A696]/30"
                      } bg-white px-4 py-3 pr-10 text-[#1B3C5C] appearance-none focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20 disabled:bg-gray-100 disabled:cursor-not-allowed`}
                    >
                      <option value="">
                        {!step2Data.state ? "Select state first" :
                         loadingCities ? "Loading..." : "Select City"}
                      </option>
                      {cities.map((city) => (
                        <option key={city.code} value={city.code}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                    {loadingCities ? (
                      <Loader2 className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] animate-spin" />
                    ) : (
                      <ChevronDown className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] pointer-events-none" />
                    )}
                  </div>
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-500">{errors.city}</p>
                  )}
                </div>
              </div>

               <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Venue <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="venue"
                  value={step2Data.venue}
                  onChange={handleChange}
                  placeholder="Enter complete venue address"
                  rows={3}
                  maxLength={500}
                  className={`w-full h-12 rounded-xl border ${
                    errors.venue ? "border-red-500" : "border-[#F4A696]/30"
                  } bg-white px-4 py-1 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20 resize-none`}
                />
                {errors.venue && (
                  <p className="mt-1 text-sm text-red-500">{errors.venue}</p>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Google Maps Link <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="location"
                  value={step2Data.location}
                  onChange={handleChange}
                  placeholder="Enter complete venue address"
                  rows={3}
                  maxLength={500}
                  className={`w-full rounded-xl border ${
                    errors.location ? "border-red-500" : "border-[#F4A696]/30"
                  } bg-white px-4 py-3 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20 resize-none`}
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                )}
              </div>

              {/* What to Expect - Jodit Editor */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  What to Expect
                </label>
                <div className={`rounded-xl border ${
                  errors.whatToExpect ? "border-red-500" : "border-[#F4A696]/30"
                } overflow-hidden jodit-custom-wrapper`}>
                  <JoditEditor
                    value={step2Data.whatToExpect}
                    config={joditConfigShort}
                    onBlur={(newContent) => {
                      setStep2Data((prev) => ({ ...prev, whatToExpect: newContent }));
                      if (errors.whatToExpect) {
                        setErrors((prev) => ({ ...prev, whatToExpect: "" }));
                      }
                    }}
                    onChange={() => {}} // Required but we use onBlur
                  />
                </div>
                {errors.whatToExpect && (
                  <p className="mt-1 text-sm text-red-500">{errors.whatToExpect}</p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Tags (Max 20)
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696]" />
                    <input
                      type="text"
                      value={currentTag}
                      onChange={(e) => setCurrentTag(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Add tags (press Enter)"
                      disabled={step2Data.tags.length >= 20}
                      className="w-full rounded-xl border border-[#F4A696]/30 bg-white pl-10 pr-4 py-3 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20 disabled:opacity-50 disabled:bg-gray-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addTag}
                    disabled={step2Data.tags.length >= 20 || !currentTag.trim()}
                    className="rounded-xl bg-gradient-to-r from-[#F4A696] to-[#E89580] px-6 py-3 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>

                {step2Data.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {step2Data.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="flex items-center gap-2 rounded-full bg-[#FFF4F0] px-3 py-1 text-sm text-[#F4A696] border border-[#F4A696]/20"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:text-red-500 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {errors.tags && (
                  <p className="mt-1 text-sm text-red-500">{errors.tags}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Additional Details */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in-up">
              <h3 className="text-lg font-semibold text-[#1B3C5C] flex items-center gap-2">
                <Users className="h-5 w-5 text-[#F4A696]" />
                Additional Details
              </h3>

              {/* Audience */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Target Audience
                </label>
                <div className="relative">
                  <select
                    name="audience"
                    value={step3Data.audience}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-[#F4A696]/30 bg-white px-4 py-3 pr-10 text-[#1B3C5C] appearance-none focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
                  >
                    <option value="all">All</option>
                    <option value="adults">Adults</option>
                    <option value="children">Children</option>
                    <option value="teens">Teens</option>
                    <option value="seniors">Seniors</option>
                    <option value="professionals">Professionals</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] pointer-events-none" />
                </div>
              </div>

              {/* Age Restrictions Section */}
              <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-200/50">
                <h4 className="text-sm font-semibold text-[#1B3C5C] mb-3 flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-purple-600" />
                  Age Restrictions (Optional)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#1B3C5C] mb-2">
                      Minimum Age
                    </label>
                    <div className="relative">
                      <UserCheck className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-purple-600" />
                      <input
                        type="number"
                        value={step3Data.age.minAge}
                        onChange={(e) => handleAgeChange(e, 'minAge')}
                        placeholder="e.g., 18"
                        min="0"
                        max="120"
                        className={`w-full rounded-lg border ${
                          errors['age.minAge'] ? "border-red-500" : "border-purple-300/50"
                        } bg-white pl-9 pr-4 py-2.5 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
                      />
                    </div>
                    {errors['age.minAge'] && (
                      <p className="mt-1 text-xs text-red-500">{errors['age.minAge']}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#1B3C5C] mb-2">
                      Maximum Age
                    </label>
                    <div className="relative">
                      <UserCheck className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-purple-600" />
                      <input
                        type="number"
                        value={step3Data.age.maxAge}
                        onChange={(e) => handleAgeChange(e, 'maxAge')}
                        placeholder="e.g., 65"
                        min="0"
                        max="120"
                        className={`w-full rounded-lg border ${
                          errors['age.maxAge'] ? "border-red-500" : "border-purple-300/50"
                        } bg-white pl-9 pr-4 py-2.5 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
                      />
                    </div>
                    {errors['age.maxAge'] && (
                      <p className="mt-1 text-xs text-red-500">{errors['age.maxAge']}</p>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-xs text-purple-700/70">
                  Leave empty for no age restrictions. Setting only minimum age allows all ages above it.
                </p>
              </div>

              {/* Show Dummy Users Toggle Switch */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200/50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-[#1B3C5C] flex items-center gap-2">
                      <Users className="h-4 w-4 text-indigo-600" />
                      Show Dummy Users
                    </h4>
                    <p className="mt-1 text-xs text-indigo-700/70">
                      Display sample registered users on the event details page (15 females, 5 males)
                    </p>
                  </div>
                  
                  {/* Toggle Switch */}
                  <button
                    type="button"
                    onClick={handleToggleSwitch}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      step3Data.showDummyUsers ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        step3Data.showDummyUsers ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Status Indicator */}
                <div className="mt-3 flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${step3Data.showDummyUsers ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-xs font-medium text-[#1B3C5C]">
                    {step3Data.showDummyUsers ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

               {/* appliedGst or not Toggle Switch */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200/50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-[#1B3C5C] flex items-center gap-2">
                      <Users className="h-4 w-4 text-indigo-600" />
                      Apply GST
                    </h4>
                    <p className="mt-1 text-xs text-indigo-700/70">
                      Toggle whether GST is applied to the event pricing
                    </p>
                  </div>
                  
                  {/* Toggle Switch */}
                  <button
                    type="button"
                    onClick={handlegstToggleSwitch}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      step3Data.appliedGst ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        step3Data.appliedGst ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Status Indicator */}
                <div className="mt-3 flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${step3Data.appliedGst ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-xs font-medium text-[#1B3C5C]">
                    {step3Data.appliedGst ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Capacity */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Event Capacity
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696]" />
                  <input
                    type="number"
                    name="capacity"
                    value={step3Data.capacity}
                    onChange={handleChange}
                    placeholder="Maximum attendees (optional)"
                    min="1"
                    className="w-full rounded-xl border border-[#F4A696]/30 bg-white pl-10 pr-4 py-3 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
                  />
                </div>
                {errors.capacity && (
                  <p className="mt-1 text-sm text-red-500">{errors.capacity}</p>
                )}
              </div>

              {/* Community Links */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Community Links
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696]" />
                  <input
                    type="text"
                    name="communityLinks"
                    value={step3Data.communityLinks}
                    onChange={handleChange}
                    placeholder="Add community links"
                    className="w-full rounded-xl border border-[#F4A696]/30 bg-white pl-10 pr-4 py-3 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
                  />
                </div>
                {errors.communityLinks && (
                  <p className="mt-1 text-sm text-red-500">{errors.communityLinks}</p>
                )}
              </div>

              {/* Male Pricing Section */}
              <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-200/50">
                <h4 className="text-sm font-semibold text-[#1B3C5C] mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                  Male Ticket Pricing
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#1B3C5C] mb-2">
                      Regular Price
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#F4A696]" />
                      <input
                        type="number"
                        value={step3Data.malePrice.price}
                        onChange={(e) => handlePriceChange(e, 'malePrice', 'price')}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full rounded-lg border border-[#F4A696]/30 bg-white pl-9 pr-4 py-2.5 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
                      />
                    </div>
                    {errors['malePrice.price'] && (
                      <p className="mt-1 text-xs text-red-500">{errors['malePrice.price']}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#1B3C5C] mb-2">
                      Discounted Price (Optional)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-600" />
                      <input
                        type="number"
                        value={step3Data.malePrice.discountedPrice}
                        onChange={(e) => handlePriceChange(e, 'malePrice', 'discountedPrice')}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full rounded-lg border border-green-300/50 bg-white pl-9 pr-4 py-2.5 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20"
                      />
                    </div>
                    {errors['malePrice.discountedPrice'] && (
                      <p className="mt-1 text-xs text-red-500">{errors['malePrice.discountedPrice']}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Female Pricing Section */}
              <div className="bg-pink-50/50 rounded-xl p-4 border border-pink-200/50">
                <h4 className="text-sm font-semibold text-[#1B3C5C] mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-pink-600" />
                  Female Ticket Pricing
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#1B3C5C] mb-2">
                      Regular Price
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#F4A696]" />
                      <input
                        type="number"
                        value={step3Data.femalePrice.price}
                        onChange={(e) => handlePriceChange(e, 'femalePrice', 'price')}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full rounded-lg border border-[#F4A696]/30 bg-white pl-9 pr-4 py-2.5 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
                      />
                    </div>
                    {errors['femalePrice.price'] && (
                      <p className="mt-1 text-xs text-red-500">{errors['femalePrice.price']}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#1B3C5C] mb-2">
                      Discounted Price (Optional)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-600" />
                      <input
                        type="number"
                        value={step3Data.femalePrice.discountedPrice}
                        onChange={(e) => handlePriceChange(e, 'femalePrice', 'discountedPrice')}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="w-full rounded-lg border border-green-300/50 bg-white pl-9 pr-4 py-2.5 text-[#1B3C5C] placeholder:text-[#1B3C5C]/40 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20"
                      />
                    </div>
                    {errors['femalePrice.discountedPrice'] && (
                      <p className="mt-1 text-xs text-red-500">{errors['femalePrice.discountedPrice']}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Currency
                </label>
                <div className="relative">
                  <select
                    name="currency"
                    value={step3Data.currency}
                    onChange={handleChange}
                    disabled={loadingCurrencies}
                    className="w-full rounded-xl border border-[#F4A696]/30 bg-white px-4 py-3 pr-10 text-[#1B3C5C] appearance-none focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20 disabled:bg-gray-100"
                  >
                    {currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name} ({currency.symbol})
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] pointer-events-none" />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-[#1B3C5C] mb-2">
                  Event Status
                </label>
                <div className="relative">
                  <select
                    name="status"
                    value={step3Data.status}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-[#F4A696]/30 bg-white px-4 py-3 pr-10 text-[#1B3C5C] appearance-none focus:border-[#F4A696] focus:outline-none focus:ring-2 focus:ring-[#F4A696]/20"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#F4A696] pointer-events-none" />
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer Actions */}
        <div className="sticky bottom-0 border-t border-[#F4A696]/20 bg-white px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={step === 1 ? onClose : handlePrevious}
              disabled={isLoading}
              className="rounded-xl border-2 border-[#F4A696]/30 px-6 py-2.5 text-[#F4A696] font-medium hover:bg-[#FFF4F0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {step === 1 ? "Cancel" : "Previous"}
            </button>

            <div className="flex gap-2">
              {step < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="rounded-xl bg-gradient-to-r from-[#F4A696] to-[#E89580] px-8 py-2.5 text-white font-medium hover:shadow-lg transition-all"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="rounded-xl bg-gradient-to-r from-[#F4A696] to-[#E89580] px-8 py-2.5 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Saving Event...</span>
                    </>
                  ) : (
                    <>{initialData ? "Update Event" : "Create Event"}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles for Jodit */}
      <style jsx global>{`
        .jodit-custom-wrapper .jodit-container {
          border: none !important;
        }
        
        .jodit-custom-wrapper .jodit-toolbar-button {
          background-color: #FFF4F0 !important;
          border-radius: 8px !important;
          margin: 2px !important;
        }
        
        .jodit-custom-wrapper .jodit-toolbar-button:hover {
          background-color: #F4A696 !important;
          color: white !important;
        }
        
        .jodit-custom-wrapper .jodit-toolbar-button_active {
          background-color: #F4A696 !important;
          color: white !important;
        }
        
        .jodit-custom-wrapper .jodit-wysiwyg {
          font-family: inherit !important;
          padding: 1rem !important;
          min-height: 200px !important;
        }
        
        .jodit-custom-wrapper .jodit-workplace {
          border-radius: 0.75rem !important;
        }
        
        .jodit-custom-wrapper .jodit-status-bar {
          display: none !important;
        }

        .jodit-custom-wrapper .jodit-toolbar {
          background-color: #FFF4F0 !important;
          border-bottom: 1px solid rgba(244, 166, 150, 0.3) !important;
          border-radius: 0.75rem 0.75rem 0 0 !important;
          padding: 8px !important;
        }
      `}</style>
    </div>
  );
}
